import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Send, ArrowLeft, User, MessageCircle, ShoppingCart } from 'lucide-react';
import api from '../lib/api';

export default function ChatPage() {
  const { conversationId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeConv, setActiveConv] = useState(conversationId || null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get('/chat/conversations').then(r => setConversations(r.data.conversations)).catch(() => {});
  }, []);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const { data } = await api.get(`/chat/messages/${convId}`);
      setMessages(prev => {
        // Only scroll if there are new messages
        if (data.messages.length !== prev.length) {
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
        return data.messages;
      });
    } catch {}
  }, []);

  useEffect(() => {
    if (activeConv) {
      loadMessages(activeConv);
      // Poll for new messages every 3 seconds
      pollRef.current = setInterval(() => loadMessages(activeConv), 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [activeConv, loadMessages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConv) return;
    setSending(true);
    try {
      await api.post('/chat/messages', { conversation_id: activeConv, text: newMessage });
      setNewMessage('');
      loadMessages(activeConv);
    } catch {}
    setSending(false);
  };

  const selectConversation = (convId) => {
    setActiveConv(convId);
    navigate(`/chat/${convId}`, { replace: true });
  };

  const activeConversation = conversations.find(c => c._id === activeConv);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="chat-page">
      <h1 className="text-2xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>Mensagens</h1>

      <div className="flex border border-border rounded-xl overflow-hidden bg-card" style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
        {/* Conversations list */}
        <div className={`${activeConv ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r border-border`}>
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold text-sm">Conversas</h3>
          </div>
          <ScrollArea className="flex-1">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                Nenhuma conversa ainda
              </div>
            ) : (
              conversations.map(conv => (
                <button
                  key={conv._id}
                  onClick={() => selectConversation(conv._id)}
                  className={`w-full flex items-center gap-3 p-4 border-b border-border hover:bg-muted/50 transition-colors ${activeConv === conv._id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                  data-testid={`conv-${conv._id}`}
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-medium text-sm truncate">{conv.other_user?.name || 'Usuário'}</p>
                    <p className="text-xs text-muted-foreground truncate">{conv.last_message?.text || 'Sem mensagens'}</p>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5 font-semibold">{conv.unread_count}</span>
                  )}
                </button>
              ))
            )}
          </ScrollArea>
        </div>

        {/* Messages area */}
        <div className={`${activeConv ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
          {activeConv ? (
            <>
              <div className="flex items-center gap-3 p-4 border-b border-border">
                <button onClick={() => setActiveConv(null)} className="md:hidden p-1">
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <p className="font-medium text-sm flex-1">{activeConversation?.other_user?.name || 'Usuário'}</p>
                {activeConversation?.product_id && (
                  <Link to={`/products/${activeConversation.product_id}`}>
                    <Button variant="outline" size="sm" className="rounded-lg gap-1.5 text-xs" data-testid="chat-go-to-product">
                      <ShoppingCart className="h-3.5 w-3.5" /> Ver produto
                    </Button>
                  </Link>
                )}
              </div>

              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3">
                  {messages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.sender_id === user?._id ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${msg.sender_id === user?._id ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
                        <p>{msg.text}</p>
                        <p className={`text-[10px] mt-1 ${msg.sender_id === user?._id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                          {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              <form onSubmit={handleSend} className="flex items-center gap-2 p-4 border-t border-border" data-testid="chat-send-form">
                <Input
                  placeholder="Digite sua mensagem..."
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  className="flex-1"
                  data-testid="chat-message-input"
                />
                <Button type="submit" size="icon" disabled={sending || !newMessage.trim()} className="rounded-full shrink-0" data-testid="chat-send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Selecione uma conversa</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
